import { Popover, Stack, Text } from '@mantine/core';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import useStaticEditorUsage from '@/hooks/api/datasets/static/useStaticEditorUsage.ts';
import { StaticDataset } from '@/types/datasets.ts';

export default function StaticDatasetDependenciesPopover({
  dataset,
  name = dataset.available_dataset?.name ?? dataset.dataset.name,
}: {
  dataset: StaticDataset;
  name?: string;
}) {
  const [opened, setOpened] = useState(false);
  const [pinned, setPinned] = useState(false);
  const target = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dismissing = useRef(false);
  const usage = useStaticEditorUsage(dataset.id, opened);
  const label = (state?: string) => {
    if (usage.isFetching) return 'Checking…';
    if (usage.isError || usage.fetchStatus === 'paused') return 'Couldn’t check';
    if (state === 'used') return 'Yes';
    if (state === 'unused') return 'No';
    if (state === 'not_published') return 'Not published yet';
    return 'Couldn’t check';
  };
  const clearTimer = () => clearTimeout(timer.current);
  useEffect(() => () => clearTimeout(timer.current), []);
  const preview = () => {
    clearTimer();
    if (!dismissing.current) setOpened(true);
  };
  const leave = () => {
    clearTimer();
    timer.current = setTimeout(() => {
      if (
        !pinned &&
        !target.current?.contains(document.activeElement) &&
        !panel.current?.contains(document.activeElement)
      )
        setOpened(false);
    }, 250);
  };
  const dismiss = (returnFocus = false) => {
    clearTimer();
    setPinned(false);
    dismissing.current = true;
    setOpened(false);
    if (returnFocus) target.current?.querySelector('button')?.focus();
    queueMicrotask(() => {
      dismissing.current = false;
    });
  };
  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && opened) {
      event.stopPropagation();
      dismiss(true);
    }
  };
  return (
    <Popover
      opened={opened}
      onDismiss={() => dismiss()}
      closeOnEscape={false}
      clickOutsideCapture
      position="bottom-end"
      width={340}
      withinPortal
      trapFocus={pinned}
    >
      <Popover.Target>
        <span
          ref={target}
          onMouseEnter={preview}
          onMouseLeave={leave}
          onFocus={preview}
          onBlur={leave}
          onKeyDownCapture={handleEscape}
        >
          <StyledButton
            variant="subtleIcon"
            size="sm"
            aria-label={`Data source usage for ${name}`}
            aria-expanded={opened}
            onClick={() => {
              if (pinned) dismiss();
              else {
                setPinned(true);
                setOpened(true);
              }
            }}
            styles={{
              root: { color: 'var(--mantine-color-gray-7)', backgroundColor: 'transparent' },
            }}
          >
            <HiOutlineInformationCircle size={16} />
          </StyledButton>
        </span>
      </Popover.Target>
      <Popover.Dropdown
        aria-label="Data source usage"
        ref={panel}
        onMouseEnter={clearTimer}
        onMouseLeave={leave}
        onFocus={clearTimer}
        onBlur={leave}
        onKeyDownCapture={handleEscape}
        style={{ maxWidth: 'calc(100vw - 24px)', maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Stack gap="xs">
          <Text size="sm" tabIndex={-1} data-autofocus>
            Used by latest draft: {label(usage.data?.draft)}
          </Text>
          <Text size="sm">Used by published version: {label(usage.data?.published)}</Text>
          {(usage.isError ||
            usage.fetchStatus === 'paused' ||
            usage.data?.draft === 'unknown' ||
            usage.data?.published === 'unknown') && (
            <StyledButton
              variant="outline"
              size="sm"
              onClick={() => void usage.refetch()}
              disabled={usage.isFetching}
            >
              Retry usage check
            </StyledButton>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
